import { useEffect, useRef } from "react";
import { RegisterLink } from "@/components/RegisterLink";

export default function HeroBanner() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = globalThis;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      heroRef.current.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
    };
    globalThis.addEventListener("mousemove", handleMouseMove);
    return () => globalThis.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-[88vh] md:min-h-[72vh] flex flex-col items-center justify-center overflow-hidden pt-20">
      {/* Ambient orbs */}
      <div className="orb w-[600px] h-[600px] top-[-200px] left-[-200px] animate-pulse-glow"
        style={{ background: "radial-gradient(circle, rgba(26,58,143,0.25) 0%, transparent 70%)" }}
      ></div>
      <div className="orb w-[500px] h-[500px] top-[-100px] right-[-150px] animate-pulse-glow"
        style={{ background: "radial-gradient(circle, rgba(107,33,168,0.2) 0%, transparent 70%)", animationDelay: "1.5s" }}
      ></div>
      <div className="orb w-[400px] h-[400px] bottom-[0px] left-[30%] animate-pulse-glow"
        style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)", animationDelay: "3s" }}
      ></div>

      {/* Light streaks */}
      <div className="streak" style={{ top: "30%", left: 0 }}></div>
      <div className="streak streak-2" style={{ top: "55%", left: 0 }}></div>
      <div className="streak streak-3" style={{ top: "70%", left: 0 }}></div>

      {/* Geometric lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-10">
        <div className="absolute top-1/4 left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }}
        ></div>
        <div className="absolute top-3/4 left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(107,33,168,0.5), transparent)" }}
        ></div>
        <div className="absolute top-0 bottom-0 left-1/4 w-px"
          style={{ background: "linear-gradient(180deg, transparent, rgba(201,168,76,0.3), transparent)" }}
        ></div>
        <div className="absolute top-0 bottom-0 right-1/4 w-px"
          style={{ background: "linear-gradient(180deg, transparent, rgba(107,33,168,0.3), transparent)" }}
        ></div>
      </div>

      {/* Hero content */}
      <div
        ref={heroRef}
        className="relative z-10 px-6 max-w-6xl mx-auto transition-transform duration-100 ease-out w-full"
      >
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_360px] lg:grid-cols-[minmax(0,1fr)_420px] gap-10 items-center">
          {/* Left: existing hero content */}
          <div className="text-center md:text-left">
            {/* Eyebrow */}
            <div className="flex items-center justify-center md:justify-start gap-3 mb-8">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]/60"></div>
              <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Australia&apos;s Premier Online Casino</span>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]/60"></div>
            </div>

            <h1
              className="text-white/95 font-semibold text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-6"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                textShadow: "0 0 28px rgba(201,168,76,0.08)",
              }}
            >
              <span className="block">Rolex9 Australia</span>
              <span
                className="block text-white/80 font-normal text-xl sm:text-2xl md:text-3xl mt-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em" }}
              >
                Premium Online Casino Experience
              </span>
            </h1>

            {/* Subtitle line */}
            <div className="light-line w-48 mx-auto md:mx-0 mb-6"></div>

            {/* Troye Sivan Brand Ambassador Badge */}
            <div
              className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: "linear-gradient(135deg, rgba(201,168,76,0.08), rgba(107,33,168,0.12))",
                border: "1px solid rgba(201,168,76,0.25)",
              }}
            >
              <div className="flex flex-col items-start">
                <span className="text-[#c9a84c]/50 text-[9px] tracking-[0.25em] uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Brand Ambassador 2025/27</span>
                <span
                  className="text-white/85 text-sm font-bold tracking-[0.15em] uppercase"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Troye Sivan
                </span>
              </div>
              <div className="h-8 w-px bg-[#c9a84c]/20"></div>
              <div className="flex flex-col items-start">
                <span className="text-[#c9a84c]/50 text-[9px] tracking-[0.25em] uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Licensed by</span>
                <span className="text-white/70 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>PAGCOR &amp; Gaming Curacao</span>
              </div>
            </div>

            {/* Tagline */}
            <p
              className="text-white/50 text-sm md:text-base tracking-[0.2em] uppercase mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 300 }}
            >
              Deal The Cards · Roll The Dice · Ignite Joy
            </p>
            <p className="text-white/35 text-xs md:text-sm tracking-widest uppercase mb-10">
              Australia&apos;s Premier Online Casino &nbsp;·&nbsp; 24/7 Live Chat &nbsp;·&nbsp; AUD 19 Min. Deposit
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-4 mb-12">
              <RegisterLink section="hero" label="Register Now" className="btn-glow px-10 py-4 rounded-xl text-sm w-full sm:w-auto text-center">
                Register Now
              </RegisterLink>
              <RegisterLink section="hero" label="Explore Games" className="btn-outline-gold px-10 py-4 rounded-xl text-sm w-full sm:w-auto text-center">
                Explore Games
              </RegisterLink>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-8 md:gap-14">
              {[
                { value: "AUD 19", label: "Min. Deposit" },
                { value: "5%", label: "Daily Rebate" },
                { value: "5%", label: "Weekly Commission" },
                { value: "24/7", label: "Live Support" },
              ].map((stat) => (
                <div key={stat.label} className="text-center md:text-left">
                  <div
                    className="text-2xl md:text-3xl font-bold mb-1"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      background: "linear-gradient(135deg, #c9a84c, #e8c97a)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-white/35 text-xs tracking-widest uppercase">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: mini SEO blog card (desktop) */}
          <div className="hidden md:block">
            <article
              className="relative rounded-2xl p-6 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(3,7,33,0.72), rgba(10,5,40,0.72))",
                border: "1px solid rgba(201,168,76,0.18)",
                boxShadow: "0 0 28px rgba(201,168,76,0.06)",
              }}
            >
              <div
                className="absolute inset-0 opacity-40 pointer-events-none"
                style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.06), rgba(107,33,168,0.08))" }}
              ></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span
                    className="text-[10px] tracking-[0.25em] uppercase font-semibold"
                    style={{ fontFamily: "'Space Grotesk', sans-serif", color: "rgba(201,168,76,0.75)" }}
                  >
                    Quick Notes
                  </span>
                  <span className="text-white/25 text-[10px] tracking-widest uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Rolex9 Online Casino Australia
                  </span>
                </div>

                <h2
                  className="text-white/90 font-bold text-lg leading-snug mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Rolex9: A premium online casino built for Australia
                </h2>

                <p className="text-white/40 text-xs leading-relaxed mb-4">
                  When people say a casino is reputable, they usually mean it behaves consistently: games run smoothly, payments feel predictable, and support is reachable. Rolex9 leans into those basics and keeps the experience clean.
                </p>

                <h3
                  className="text-white/70 font-semibold text-[11px] tracking-widest uppercase mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  What stands out
                </h3>
                <p className="text-white/42 text-xs leading-relaxed mb-3">
                  The platform is easy to navigate, and the promotions are grouped in a way that makes sense. You don&apos;t need to hunt through long pages to figure out what applies to you.
                </p>
                <p className="text-white/38 text-xs leading-relaxed mb-5">
                  On desktop it feels premium and spacious, while on mobile it stays fast and readable. That balance is what many Australian players look for when choosing a reputable online casino.
                </p>

                <div className="flex items-center gap-3">
                  <a
                    href="#seo-content"
                    className="btn-outline-gold px-4 py-2 rounded-lg text-[11px] tracking-widest uppercase"
                  >
                    Read more
                  </a>
                  <span className="text-white/25 text-[11px] tracking-wide">
                    Reputable feel, premium style.
                  </span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

    </section>
  );
}
